import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import DynamicForm from "components/DynamicForm";
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Col, Container, Input, InputGroup, InputGroupText, Row } from "reactstrap";
import costFormConfig from 'formConfigs/addPackageCost';
import Heading from "components/Heading";
import { errorHandler } from "utils/Api";
import { showAlert } from "utils/Alert";
import { getPackage, savePackageCost } from "api/PackagesApi";
import TotalSummaryTable from "components/TotalSummaryTable";
import { EMAIL_COST_ENTRIES_KEY, IVA_PERCENTAGES, SHIPPING_COST_PRODUCT_ID } from "utils/Constants";
import { getPackageFinalWeight, getSummaryData } from "utils/Utils";
import { getUserData } from "utils/Session";
import { getPrices } from "api/PriceApi";
import TooltipIcon from "components/TooltipIcon";
import styles from "./style.module.scss";
import { post as postAPICall } from 'aws-amplify/api';
import { User } from "utils/User";
import { makeFormInputsUneditable } from "components/DynamicForm/utils";
import ConfirmDeleteButton from "components/ConfirmDeleteButton";
import EasyposLogsOffCanvas from "components/OffCanvas/EasyposLogsOffCanvas";
import moment from "moment";
import { getPoBoxReferalPointes, savePoBoxReferalPoint } from "api/PoBoxReferalPointAPI";

const AddEditPackageCost = props => {
  let navigate = useNavigate();
  let { id: packageId } = useParams();
  const [referalAvailablePoints, setReferalAvailablePoints] = useState(0);
  const [isReferalError, setReferalError] = useState(false);
  const [packageCostData, setPackageCostData] = useState({});
  const [packagePoBox, setPackagePoBox] = useState({});
  const [packageData, setpackageData] = useState({});
  const [formConfig, setFormConfig] = useState(costFormConfig);
  const [pricesList, setPricesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [referalCredit, setReferalCredit] = useState(0);
  const costForm = useRef();
  const invoiceId = packageCostData?.invoiceId || false;
  const settlementId = packageCostData?.settlementId || false;
  const isFormNonEditable = (invoiceId || settlementId);

  const DEFAULT_COST_DATA = [{
    "cant": 1,
    "codigo": SHIPPING_COST_PRODUCT_ID,
    "key": EMAIL_COST_ENTRIES_KEY.SHIPPING_COST,
    "description": "FLETE MIAMI A SAN JOSE",
    "price": 0,
    "iva": "01",
    "comision": 0,
    "discount": 0
  }, {
    "cant": 1,
    "codigo": 55,
    "key": EMAIL_COST_ENTRIES_KEY.TAX_COST,
    "description": "PAGOS A TERCEROS ",
    "price": 0,
    "iva": "01",
    "comision": 0,
    "discount": 0
  },
  {
    "cant": 1,
    "codigo": 19,
    "key": EMAIL_COST_ENTRIES_KEY.LOCAL_SHIPPING_COST,
    "description": "COSTO ENVIO ",
    "price": 0,
    "iva": "01",
    "comision": 0,
    "discount": 0
  }]

  useLayoutEffect(() => {
    if (packageId) {
      reloadPackageData(packageId)
    }
  }, [packageId]);

  const reloadPackageData = (packageId) => {
    setIsLoading(true);
    loadPricesData();
    loadPoBoxReferalPoints();
    loadpackageCostData(packageId);
  }

  useEffect(() => {
    setTimeout(() => {
      if (isFormNonEditable) {
        setFormConfig(makeFormInputsUneditable(costFormConfig, ["priceType", "data", "exchangeRate"]));
      } else {
        setFormConfig(costFormConfig);
      }
    });
  }, [isFormNonEditable]);

  const loadPoBoxReferalPoints = async (poBoxId) => {
    if (poBoxId) {
      const referalPoints = await getPoBoxReferalPointes({ poBoxReferalPointReferalPoBoxId: { eq: poBoxId } }, "ASC");
      const availablePoints = (referalPoints || []).reduce((a, v) => {
        return a + new Number(v.amount);
      }, 0);
      setReferalAvailablePoints(availablePoints || 0)
    } else {
      setReferalAvailablePoints(0)
    }
  }

  const loadPricesData = () => {
    getPrices().then(prices => {
      setPricesList(prices);
    }).catch((e) => console.log("fetch prices list data error", e));
  }

  const loadpackageCostData = (packageId) => {
    getPackage(packageId).then((packageObject) => {
      const {
        secuencia, warehouseNumber, provedor, client, tipo_transporte,
        tracking_num, courier, cost, measures, weigth, vweigth } = packageObject;

      const packageData = {
        secuencia, warehouseNumber, provedor, client, tipo_transporte,
        tracking_num, courier, cost, measures, weigth, vweigth
      }

      setpackageData(packageData);
      const summary = calculateSummaryData();
      const defaultState = { ...summary, data: JSON.stringify(DEFAULT_COST_DATA) };
      setPackageCostData(packageObject.boxCost || defaultState);
      setPackagePoBox(packageObject.poBox || {});
      loadPoBoxReferalPoints(packageObject.poBox.id);
    }).catch((e) => console.log("fetch package cost data error", e))
      .finally(() => { setIsLoading(false) });
  }

  const onSubmitForm = async (formData) => {
    if (isFormNonEditable) {
      showAlert("danger", "Para poder actualizar el costo del paquete, el paquete no tiene que tener una factura creada");
      return false;
    }
    try {
      await addPackageCost(formData)
    } catch (e) {
      errorHandler(e);
      showAlert("danger", "No fue posible guardar la informacion del costo del paquete");
    }
  }

  const addPackageCost = async (formData, redirect = true) => {
    setIsLoading(true);
    const { id, invoiceId, settlementId, data, easyposLog, subtotal, iva, discount,
      comision, total, boxCostBoxId = packageId, priceType, exchangeRate
    } = formData;
    const packageCostData = {
      id, invoiceId, settlementId, data, easyposLog, subtotal, iva,
      discount, comision, total, boxCostBoxId, priceType, exchangeRate
    };

    await savePackageCost(packageCostData);
    showAlert("success",
      `El costo del Paquete ha sido ${((packageCostData && packageCostData.id) ? "actualizado" : "creado")} exitosamente`
    );
    if (redirect) {
      navigate("/admin/packages");
    }
    setIsLoading(false);
  };

  const onInputChange = (fieldName, value) => {
    if (fieldName === "data") {
      updateTableData(value);
    }
    else if (fieldName === "priceType") {
      const additionalData = { priceType: value };
      calculatePackageCost(value, additionalData);
    } else {
      const newPackageCostData = {
        ...packageCostData,
        [fieldName]: value
      }
      setPackageCostData(newPackageCostData);
    }
  }

  const updateTableData = (newData, additionalData = {}) => {
    const summary = calculateSummaryData(newData);
    const newPackageCostData = {
      ...packageCostData,
      ...summary,
      ...additionalData,
      data: newData
    }
    setPackageCostData(newPackageCostData);

  }

  const calculatePackageCost = (priceId, additionalData = {}) => {
    const priceType = pricesList.find(p => p.id === priceId);
    const productsData = JSON.parse(packageCostData.data);

    productsData.forEach((p, index) => {
      if (p.codigo === SHIPPING_COST_PRODUCT_ID) {
        const packageWeight = getPackageFinalWeight(packageData.weigth, packageData.vweigth);
        const priceTable = JSON.parse(priceType.priceTable);
        const priceFactor = priceTable.reduce((a, p) => {
          return (Math.abs(p.weight - packageWeight) < Math.abs(a.weight - packageWeight) ? p : a);
        }, { weight: 0, price: 0 });
        const shippintCost = Number(priceFactor.price * packageWeight).toFixed(2);
        productsData[index].price = shippintCost;
      }
    })
    updateTableData(JSON.stringify(productsData), additionalData);
  }

  const calculateSummaryData = (tableData) => {
    let summary = {
      subtotal: Number(0),
      iva: Number(0),
      discount: Number(0),
      comision: Number(0),
      total: Number(0),
    }
    if (tableData) {
      const costlines = JSON.parse(tableData);
      summary = costlines.reduce((a, l) => {
        const price = Number(l.price || 0);
        const comision = Number(l.comision || 0);
        const discount = Number(l.discount || 0) / 100;
        const discountAmount = price * discount;
        const iva = (price - discountAmount) * IVA_PERCENTAGES[l.iva];

        a.subtotal += price;
        a.discount += discountAmount;
        a.comision += comision;
        a.iva += iva;
        a.total = a.subtotal + a.iva - a.discount;
        return a;
      }, summary);
    }

    return summary;
  }

  const getFormData = () => {
    return {
      ...packageCostData,
      ...packageData
    }
  }

  const getPoBoxAddress = () => {
    const poBoxAddresses = packagePoBox?.addresses?.items || [];
    const poBoxAddress = poBoxAddresses.shift() || {};
    return poBoxAddress.address || "";
  }

  const setFleetDiscountPercentage = (parsedReferalCredit) => {
    if (parsedReferalCredit > referalAvailablePoints) {
      showAlert("danger", "No es posible aplicar un credito mayor al monto disponible para canje");
      setReferalError(true);
      return;
    }
    setReferalError(false);
    setReferalCredit(parsedReferalCredit)

    const productsData = JSON.parse(packageCostData.data);
    productsData.forEach((p, index) => {
      const subTotal = p.price * p.cant;
      if (parsedReferalCredit > 0 && p.codigo === SHIPPING_COST_PRODUCT_ID) {
        if (parsedReferalCredit >= subTotal) {
          showAlert("danger", "El monto de credito de canje para la linea de transporte no puede ser mayor o igual al costo de envio")
          return;
        }
        const discountPercentage = Number(parsedReferalCredit * 100 / subTotal);
        productsData[index].discount = discountPercentage.toFixed(2);
      }
    })
    updateTableData(JSON.stringify(productsData));
    showAlert("success", "Se ha asignado el descuento a la linea de factura del flete del paquete.");
  }

  const getProductLines = (productData) => {
    const products = JSON.parse(productData);
    return products.reduce((a, p, i) => {
      const price = Number(p.price);
      const subTotal = Number(p.cant) * Number(p.price);
      const discount = Number(p.discount || 0) / 100;
      const discountAmount = price * discount;
      const iva = (price - discountAmount.toFixed(2)) * IVA_PERCENTAGES[p.iva];

      return {
        ...a,
        [`Products[${i}][InvoiceProducts][codigo]`]: p.codigo,
        [`Products[${i}][InvoiceProducts][description]`]: p.description,
        [`Products[${i}][InvoiceProducts][cant]`]: p.cant,
        [`Products[${i}][InvoiceProducts][price]`]: price.toFixed(2),
        [`Products[${i}][InvoiceProducts][subtotal]`]: subTotal.toFixed(2),
        [`Products[${i}][InvoiceProducts][impuestos]`]: iva.toFixed(2),
        [`Products[${i}][InvoiceProducts][porcentaje_descuento]`]: `${discount}`,
        [`Products[${i}][InvoiceProducts][descuento]`]: discountAmount.toFixed(2),
        [`Products[${i}][InvoiceProducts][porcentaje_impuesto]`]: `${IVA_PERCENTAGES[p.iva]}`,
        [`Products[${i}][InvoiceProducts][codigo_tarifa]`]: `${p.iva}`
      }
    }, {});
  }

  const addEasyposLogEntry = (easyposLogEntry) => {
    const easyposLog = packageCostData?.easyposLog ? JSON.parse(packageCostData.easyposLog) : [];
    return [
      ...easyposLog,
      easyposLogEntry
    ];
  }

  const cancelInvoice = async (invoiceId) => {
    setIsLoading(true);
    try {
      const postData = {
        "invoiceId": invoiceId,
      };

      const postPayload = {
        apiName: "easypos",
        path: "/cancelInvoice",
        options: {
          body: postData
        }

      };
      const restOperation = await postAPICall(postPayload).response;
      const response = await restOperation.body.json();

      if (response.stored) {
        const creditNoteMessage = response.creditNoteId && response.creditNoteId != -1 ? `, el numero de la nota de credito creada es #${response.creditNoteId}` : "";
        const easyposLogEntry = {
          created: moment().valueOf(),
          description: `Anulacion de Factura #${invoiceId} en Easypos${creditNoteMessage}`,
          invoiceId,
          creditNoteId: response.creditNoteId
        };
        const newEasyposLog = addEasyposLogEntry(easyposLogEntry);
        const newPackageCostData = {
          ...packageCostData,
          invoiceId: null,
          easyposLog: JSON.stringify(newEasyposLog)
        };
        await addPackageCost(newPackageCostData, false);
        setPackageCostData(newPackageCostData);
        showAlert("success", `La factura fue anulada correctamente en Easypos${creditNoteMessage}`)
      } else {
        showAlert("danger", "No fue posible anular la factura en Easypos, por favor intentelo mas tarde")
      }
    } catch (err) {
      errorHandler(err);
      showAlert("danger", "No fue posible anular la factura en Easypos, por favor intentelo mas tarde")
    }
    setIsLoading(false);
  }

  const createInvoice = async () => {
    setIsLoading(true);
    try {
      const form = costForm.current;
      const formData = await form.getFormData();
      if (!formData) {
        showAlert("danger", "Antes de Facturar este paquete, por favor solucione cualquier error en el formulario");
      } else {
        const productLines = getProductLines(formData.data);
        const userData = await getUserData(packagePoBox.userId);
        const user = User(userData);
        const postData = {
          "Invoice[client_id]": "0",
          "Invoice[nombre_factura]": user.getUserName(),
          "Invoice[identificacion]": user.getIdentification(),
          "Invoice[tel1]": user.getTel1(),
          "Invoice[email]": user.getEmail(),
          "Invoice[direccion]": getPoBoxAddress(),
          "Invoice[fair_id]": "0",
          "Invoice[cerrada]": "1",
          "Invoice[porcenta_descuento]": "0",
          "Invoice[subtotal]": packageCostData?.subtotal || "0",
          "Invoice[descuento]": packageCostData?.discount || "0",
          "Invoice[impuestos]": packageCostData?.iva || "0",
          "Invoice[impuestos_servicio]": "0.00",
          "Invoice[total]": packageCostData?.total || "0",
          "Invoice[tipo]": "0",
          "Invoice[einvoice_kind]": user.getEinvoiceType(),
          "Invoice[es_apartado]": "0",
          "Invoice[abono_inicial]": "0",
          "Invoice[exenta]": "0",
          "Invoice[suscriptioncurrency_id]": "2",
          "Invoice[exchange_rate]": `${formData.exchangeRate}`,
          "Invoice[with_user_info]": "0",
          "Invoice[lotaltyPointsIsAble]": "1",
          "Invoice[document_comments]": "",
          "Invoice[plazo]": "0",
          "Invoice[activitycode]": "105040", //TODO Actividad Economica por default en easypos
          "notReduceExistence": "0",
          ...productLines
        };

        const postPayload = {
          apiName: "easypos",
          path: "/createInvoice",
          options: {
            body: postData
          }

        };
        const restOperation = await postAPICall(postPayload).response;
        const response = await restOperation.body.json();

        if (response.store) {
          const easyposLogEntry = {
            created: moment().valueOf(),
            description: `Creacion de Factura #${response.invoiceId} en easypos`,
            invoiceId: response.invoiceId,
          };
          const newEasyposLog = addEasyposLogEntry(easyposLogEntry);
          const newPackageCostData = {
            ...formData,
            invoiceId: response.invoiceId,
            easyposLog: JSON.stringify(newEasyposLog)
          };

          await addPackageCost(newPackageCostData, false);
          setPackageCostData(newPackageCostData);
          if (referalCredit > 0) {
            const referalPointEntry = {
              poBoxReferalPointReferalPoBoxId: packagePoBox.id,
              amount: referalCredit * -1,
              description: `Se ha realizado un canje de -$${referalCredit} en créditos por el descuento aplicado en el paquete ${packageData.secuencia}`
            };
            savePoBoxReferalPoint(referalPointEntry);
          }
          showAlert("success", `La factura fue creada correctamente en Easypos, el numero de factura es #${response.invoiceId}`)
          reloadPackageData(packageId);
        } else {
          showAlert("danger", "No fue posible crear la factura en Easypos, por favor intentelo mas tarde")
        }
      }
    } catch (err) {
      errorHandler(err);
      showAlert("danger", "No fue posible crear la factura en Easypos, por favor intentelo mas tarde")
    }
    setIsLoading(false);

  }

  return (
    <>
      <Heading yellowBg={true} headingLevel="h2" className="pt-4 pb-4" label="Costo de Paquetes" sticky bordered >
        <Col sm={2}>
          <Row>
            <Col>
              <Button color="danger" onClick={(e) => { navigate("/admin/packages") }}> Cerrar</Button>
            </Col>
            <Col >
              <Button className="float-end" form="addPackageCost" type="submit" color="success"> Guardar</Button>
            </Col>
          </Row>
        </Col>
      </Heading>
      <DynamicForm
        ref={costForm}
        id="addPackageCost"
        externalFormState={getFormData()}
        formConfig={formConfig}
        isLoading={isLoading}
        onSubmit={onSubmitForm}
        onInputChange={onInputChange}>
      </DynamicForm>
      <Container>
        {referalAvailablePoints > 0 && (
          <Row>
            <Col>
              <Alert color="info">
                <p>
                  El cliente tiene un total de $<b>{referalAvailablePoints}</b> para aplicar este credito ingrese el monto a aplicar en el siguiente valor:
                </p>
                <Row>
                  <Col sm="4" xs="9">
                    <InputGroup className="mb-2">
                      <InputGroupText>
                        $
                      </InputGroupText>
                      <Input
                        placeholder="Monto de Canje"
                        max={referalAvailablePoints}
                        type="number"
                        name="referalPoints"
                        invalid={isReferalError}
                        onChange={(e) => { setFleetDiscountPercentage(e.target.value) }}
                        autoComplete="off"
                      />
                    </InputGroup></Col>
                </Row>
                <p><b>El monto ingresado en este seccion sera reducido unicamente del valor del flete, y se reflejara en la factura en forma de descuento</b></p>
              </Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col
            sm={{
              size: 8
            }}>
            <div className={styles.invoiceButtons}>
              <Row className="align-items-center">
                <Col
                  sm={{
                    size: 5
                  }}>
                  {/* <b># Factura de producto con Impuestos:</b> */}
                  <b># Factura Easypos:</b>
                </Col>
                <Col
                  sm={{
                    size: 1
                  }}>
                  <a href={`https://easyposcr.com/invoices/printInvoice/${packageCostData.invoiceId}/0/0`} target="_blank">
                    {packageCostData.invoiceId ? `#${packageCostData.invoiceId}` : "N/A"}
                  </a>
                </Col>
                <Col>
                  {!packageCostData.invoiceId && (
                    <>
                      <Button color="primary" onClick={(e) => createInvoice()}> Generar Factura</Button>
                      <TooltipIcon>
                        Al presionar esta boton se creara una factura en Easypos con las lineas que tenga impuestos de venta.
                      </TooltipIcon>
                      {/* <TooltipIcon>
                    Al precionar esta boton se creara una factura en Easypos con las lineas que tenga impuestos de venta.
                  </TooltipIcon> */}
                    </>
                  )}
                  {packageCostData.invoiceId && (
                    <>
                      <ConfirmDeleteButton
                        primaryBtnColor="danger"
                        secondaryBtnColor="default"
                        title="Confirme Anulacion"
                        deleteMessage={`Esta seguro que desea eliminar la factura #${packageCostData.invoiceId} en Easypos`}
                        onDelete={(e) => cancelInvoice(packageCostData.invoiceId)}
                      >
                        Anular Factura
                      </ConfirmDeleteButton>
                      <TooltipIcon>
                        Al presionar esta boton se anulara la factura #{packageCostData.invoiceId} en Easypos.
                      </TooltipIcon>
                    </>
                  )}
                </Col>
              </Row>
              <Row>
                <Col>
                  <EasyposLogsOffCanvas
                    packageCostData={packageCostData}>
                    Ver Historial de Movimientos en Easypos
                  </EasyposLogsOffCanvas>
                </Col>
              </Row>
              {/* <Row className="align-items-center">
                <Col
                  sm={{
                    size: 5
                  }}>
                  <b># Liquidacion de producto sin Impuestos:</b>
                </Col>
                <Col
                  sm={{
                    size: 1
                  }}>
                  #4003
                </Col>
                <Col>
                  <Button color="primary" onClick={(e) => { navigate("/admin/packages") }}> Generar Liquidacion</Button>
                  <TooltipIcon>
                    Al precionar esta boton se creara una Liquidacion en Easypos con las lineas que no tengan impuestos de venta.
                  </TooltipIcon>
                </Col>
              </Row> */}
            </div>
          </Col>
          <Col
            sm={{
              size: 3
            }}>
            <TotalSummaryTable data={getSummaryData(packageCostData)} />
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AddEditPackageCost;
